// Auth + identity hooks (Phase 5).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import type {
  AdminAuditEvent,
  AdminPerformanceEvent,
  AdminPerformanceSummary,
  AdminProfileRow,
  Me,
  Role,
} from "./types";

// `useMe` is the SPA's identity-bootstrap call. AuthGuard reads this
// to decide whether to show the app or send the user to /login.
export function useMe(enabled = true) {
  return useQuery<Me>({
    queryKey: ["me"],
    queryFn: () => api<Me>("/api/me"),
    enabled,
    retry: false, // 401 is a normal "not logged in" — don't retry
    staleTime: 30_000,
  });
}

interface SignupBody {
  email: string;
  nom: string;
  prenom: string;
  password: string;
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SignupBody) =>
      api<{ profil_id: string; organisation_id: string }>(
        "/api/auth/signup",
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api<{ profil_id: string; organisation_id: string }>(
        "/api/auth/login",
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<void>("/api/auth/logout", { method: "POST" }),
    onSuccess: () => {
      qc.clear();
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (body: { email: string }) =>
      api<{ status: string }>(
        "/api/auth/forgot-password",
        { method: "POST", body: JSON.stringify(body) },
      ),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      api<void>(
        "/api/auth/reset-password",
        { method: "POST", body: JSON.stringify(body) },
      ),
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      api<{ profil_id: string; organisation_id: string }>(
        "/api/auth/accept-invite",
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

interface ProfilePatch {
  nom?: string;
  prenom?: string;
  fonction?: string | null;
  color?: string;
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProfilePatch) =>
      api<Me>(
        "/api/me/profile",
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return api<Me>("/api/me/avatar", { method: "POST", body: fd });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useDeleteAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<Me>("/api/me/avatar", { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useSwitchOrganisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (organisation_id: string) =>
      api<void>(
        "/api/me/switch-organisation",
        {
          method: "POST",
          body: JSON.stringify({ organisation_id }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["panneaux"] });
      qc.invalidateQueries({ queryKey: ["history"] });
    },
  });
}

// ── Admin (administrateur only) ────────────────────────────────────────

export function useAdminProfils() {
  return useQuery<AdminProfileRow[]>({
    queryKey: ["admin", "profils"],
    queryFn: () => api<AdminProfileRow[]>("/api/admin/profils"),
  });
}

export function useAdminInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string;
      nom: string;
      prenom: string;
      role: Role;
    }) =>
      api<{ profil_id: string; invite_link: string }>(
        "/api/admin/invite",
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "profils"] }),
  });
}

export function useAdminPatchProfil() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      role?: Role;
      is_active?: boolean;
    }) =>
      api<{ status: string }>(
        `/api/admin/profils/${id}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "profils"] }),
  });
}

export function useClaimLegacy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{
        panneaux_restamped: number;
        proposals_restamped: number;
        new_conversation_id: string;
      }>("/api/admin/claim-legacy", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["panneaux"] });
      qc.invalidateQueries({ queryKey: ["history"] });
    },
  });
}

export function useAdminPerformanceSummary() {
  return useQuery<AdminPerformanceSummary>({
    queryKey: ["admin", "performance", "summary"],
    queryFn: () => api<AdminPerformanceSummary>("/api/admin/performance/summary"),
  });
}

export function useAdminPerformanceEvents(eventType?: string) {
  const suffix = eventType ? `?event_type=${encodeURIComponent(eventType)}` : "";
  return useQuery<AdminPerformanceEvent[]>({
    queryKey: ["admin", "performance", "events", eventType || "all"],
    queryFn: () =>
      api<AdminPerformanceEvent[]>(`/api/admin/performance/events${suffix}`),
  });
}

export function useAdminAuditEvents() {
  return useQuery<AdminAuditEvent[]>({
    queryKey: ["admin", "audit", "events"],
    queryFn: () => api<AdminAuditEvent[]>("/api/admin/audit/events"),
  });
}
